import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteJob, getJobById, getJobs, updateJob } from "../services/jobService";
import "./AllPosts.css";

const emptyForm = {
  job_code: "",
  title: "",
  description: "",
  location: "",
  experience: "",
  employment_type: "FULL_TIME",
  is_active: true,
};

const PAGE_SIZE = 10;
const JOBS_PAGE_LIMIT = 100;

const extractJobsFromResponse = (data) => {
  if (Array.isArray(data)) {
    return data;
  }
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  if (data && Array.isArray(data.content)) {
    return data.content;
  }
  if (data && Array.isArray(data.jobs)) {
    return data.jobs;
  }
  return [];
};

const AllPosts = () => {
  const adminBasePath = "/components/Admins/AdminLayout";
  const navigate = useNavigate();

  const [jobs, setJobs] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ FIXED JOB FETCH
  const loadJobs = async () => {
    try {
      const firstPage = await getJobs({ page: 1, limit: JOBS_PAGE_LIMIT });

      console.log("All Jobs Response:", firstPage);

      const firstJobs = extractJobsFromResponse(firstPage);
      const detectedTotalPages = Number(
        firstPage?.totalPages ??
        firstPage?.data?.totalPages ??
        1
      );

      if (!Number.isFinite(detectedTotalPages) || detectedTotalPages <= 1) {
        setJobs(firstJobs);
        return;
      }

      const remainingResponses = await Promise.all(
        Array.from({ length: detectedTotalPages - 1 }, (_, index) =>
          getJobs({ page: index + 2, limit: JOBS_PAGE_LIMIT })
        )
      );

      const combinedJobs = [
        ...firstJobs,
        ...remainingResponses.flatMap(extractJobsFromResponse),
      ];

      setJobs(combinedJobs);

    } catch (error) {
      console.error("Failed to load jobs", error);
      setJobs([]);
    }
  };

  console.log("this is jobs", jobs);

  useEffect(() => {
    loadJobs();
  }, []);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(jobs.length / PAGE_SIZE));

    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [jobs, currentPage]);

  const summaryLabel = useMemo(() => {
    const activeCount = jobs.filter((job) => job.is_active).length;
    return `${jobs.length} postings | ${activeCount} active`;
  }, [jobs]);

  const totalPages = Math.max(1, Math.ceil(jobs.length / PAGE_SIZE));

  const paginatedJobs = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return jobs.slice(startIndex, startIndex + PAGE_SIZE);
  }, [jobs, currentPage]);

  const resetForm = () => {
    setEditingId(null);
    setFormData(emptyForm);
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ✅ EDIT JOB
  const handleEdit = async (job) => {
    console.log("this is job", job);

    setEditingId(job.id);
    setFormData({
      job_code: job.job_code || "",
      title: job.title || "",
      description: job.description || "",
      location: job.location || "",
      experience: job.experience ?? "",
      employment_type: job.employment_type || "FULL_TIME",
      is_active: !!job.is_active,
    });

    window.scrollTo({ top: 0, behavior: "smooth" });

    try {
      const response = await getJobById(job.id);

      const jobData =
        response?.data ||
        response?.content ||
        response ||
        {};

      setFormData({
        job_code: jobData.job_code || "",
        title: jobData.title || "",
        description: jobData.description || "",
        location: jobData.location || "",
        experience: jobData.experience ?? "",
        employment_type: jobData.employment_type || "FULL_TIME",
        is_active: !!jobData.is_active,
      });

    } catch (error) {
      console.error("Failed to load job details", error);
    }
  };

  // ✅ UPDATE JOB
  const handleUpdate = async () => {
    if (!editingId) return;

    const parsedExperience = Number(formData.experience);
    if (Number.isNaN(parsedExperience) || parsedExperience < 0) return;

    try {
      await updateJob(editingId, {
        job_code: formData.job_code,
        title: formData.title,
        description: formData.description,
        location: formData.location,
        experience: parsedExperience,
        employment_type: formData.employment_type,
        is_active: formData.is_active,
      });

      await loadJobs();
      resetForm();

    } catch (error) {
      console.error("Update failed", error);
    }
  };

  // ✅ DELETE JOB
  const handleDelete = async () => {
    if (!deleteTarget?.id) return;

    try {
      await deleteJob(deleteTarget.id);

      await loadJobs();

      if (editingId === deleteTarget.id) resetForm();
      closeDeleteDialog();

    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  // ✅ TOGGLE ACTIVE
  const toggleActive = async (id) => {
    const target = jobs.find((job) => job.id === id);
    if (!target) return;

    try {
      await updateJob(id, {
        is_active: !target.is_active,
      });

      await loadJobs();

    } catch (error) {
      console.error("Toggle failed", error);
    }
  };

  return (
    <div className="all-posts-page">
      <section className="all-posts-hero">
        <div>
          <h1>All Job Postings</h1>
          <p>Manage all posting operations from this page.</p>
          <span className="all-posts-pill">{summaryLabel}</span>
        </div>

        <button
          className="all-posts-back-btn"
          onClick={() => navigate(`${adminBasePath}/job-management`)}
        >
          Back To Job Management
        </button>
      </section>

      <section className="all-posts-list-wrap">
        {editingId !== null && (
          <section className="all-posts-edit-card">
            <div className="all-posts-edit-head">
              <div>
                <h2>Edit Job Posting</h2>
                <p>Update the selected job details.</p>
              </div>

              <button
                className="all-posts-cancel-btn"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>

            <div className="all-posts-edit-grid">
              <input
                type="text"
                name="job_code"
                placeholder="Job Code"
                value={formData.job_code}
                onChange={handleChange}
              />

              <input
                type="text"
                name="title"
                placeholder="Title"
                value={formData.title}
                onChange={handleChange}
              />

              <input
                type="text"
                name="location"
                placeholder="Location"
                value={formData.location}
                onChange={handleChange}
              />

              <input
                type="number"
                name="experience"
                placeholder="Experience"
                value={formData.experience}
                onChange={handleChange}
              />

              <select
                name="employment_type"
                value={formData.employment_type}
                onChange={handleChange}
              >
                <option value="FULL_TIME">Full-time</option>
                <option value="PART_TIME">Part-time</option>
                <option value="CONTRACT">Contract</option>
                <option value="INTERN">Internship</option>
              </select>

              <textarea
                name="description"
                placeholder="Description"
                value={formData.description}
                onChange={handleChange}
              />

              <label className="all-posts-check-row">
                is_active
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
              </label>
            </div>

            <div className="all-posts-edit-actions">
              <button
                className="all-posts-update-btn"
                onClick={handleUpdate}
              >
                Update Posting
              </button>
            </div>
          </section>
        )}

        {paginatedJobs.map((job) => (
          <article key={job.id || job.job_code} className="all-posts-card">
            <div className="all-posts-top">
              <h2>
                {job.job_code} - {job.title}
              </h2>

              <div className="all-posts-badges">
                <span className="all-posts-status">
                  {job.employment_type}
                </span>

                <span
                  className={`all-posts-active ${
                    job.is_active ? "active" : "inactive"
                  }`}
                >
                  {job.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>

            <p className="all-posts-meta">Location: {job.location}</p>

            <p className="all-posts-meta">
              Experience: {job.experience || "Not set"}
            </p>

            <p className="all-posts-desc">
              {job.description || "No description added."}
            </p>

            <div className="all-posts-actions">
              <button onClick={() => handleEdit(job)}>Edit</button>

              <button onClick={() => toggleActive(job.id)}>
                {job.is_active ? "Deactivate" : "Activate"}
              </button>

              <button
                className="danger"
                onClick={() => setDeleteTarget(job)}
              >
                Delete
              </button>
            </div>
          </article>
        ))}

        {jobs.length === 0 && (
          <div className="all-posts-empty">
            No job postings available.
          </div>
        )}

        {jobs.length > 0 && (
          <div className="all-posts-pagination">
            <button
              type="button"
              className="all-posts-page-btn"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>

            <span className="all-posts-page-indicator">
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              className="all-posts-page-btn"
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </section>

      {deleteTarget && (
        <div className="all-posts-modal-overlay" onClick={closeDeleteDialog}>
          <div
            className="all-posts-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <h2>Delete Job Posting</h2>
            <p>
              Are you sure you want to delete {deleteTarget.job_code} -{" "}
              {deleteTarget.title}?
            </p>

            <div className="all-posts-modal-actions">
              <button
                className="all-posts-cancel-btn"
                onClick={closeDeleteDialog}
              >
                Cancel
              </button>

              <button
                className="all-posts-delete-btn"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllPosts;
 
